<template>
  <v-dialog
    persistent
    v-model="show"
    :max-width="mobile ? '' : 500"
    :fullscreen="mobile"
  >
    <v-card
      elevation="2"
      class="integration-card"
      data-testid="supplier-connect-dialog"
    >
      <v-card-title class="text-wrap" data-testid="supplier-connect-title">
        <div class="d-flex align-center">
          <div
            v-if="displayConnectionSuccess && editMode"
            class="md-subheader-no-spacing"
          >
            {{ displayName }} credentials saved
          </div>
          <div
            v-else-if="displayConnectionSuccess"
            class="md-subheader-no-spacing"
          >
            {{ displayName }} connected
          </div>
          <div v-else-if="editMode" class="md-subheader-no-spacing">
            Edit {{ displayName }} credentials
          </div>
          <div v-else class="md-subheader-no-spacing">
            Connect {{ displayName }}
          </div>
          <v-spacer />
          <v-btn icon variant="plain" @click="close" :disabled="loading">
            <v-icon icon="close" /></v-btn
          >
        </div>
      </v-card-title>
      <v-card-text v-if="displayConnectionSuccess">
        <!--
          The message lives in the card body, NOT in v-card-title: Vuetify 3
          renders the title nowrap, so a sentence placed there never wraps and
          clips at the dialog edge.
        -->
        <div class="d-flex flex-column align-center text-center">
          <v-icon
            data-testid="supplier-connected-icon"
            icon="check_circle"
            color="success"
            size="64"
            class="mb-3"
          />
          <div data-testid="supplier-connected-message" class="md-body-no-spacing">
            {{ successMessage }}
          </div>
        </div>
      </v-card-text>
      <v-card-text v-else>
        <v-form v-model="valid" ref="form">
          <!--
            S&S authenticates as account number + API key and has no
            separate login, so its form leads with the account number and
            has no Username field. The server stores the account number as
            the username too (supplier_integrations._normalize_credential_args),
            which is the pair sns/credentials.py reads back.
          -->
          <v-text-field
            v-if="isSns"
            data-testid="supplier-integration-account-number"
            :rules="[validateNotEmpty, validateCredentials]"
            variant="filled"
            label="Account Number"
            v-model="accountNumber"
            @keyup="displayValidationError = false"
          />
          <!--
            Username / Account Number are deliberately NOT type="password".
            They are identifiers, not secrets; masking them would cost
            usability for no security gain (GH #215).
          -->
          <v-text-field
            v-if="!isSns"
            data-testid="supplier-integration-username"
            :rules="[validateNotEmpty, validateCredentials]"
            variant="filled"
            label="Username"
            v-model="username"
            @keyup="displayValidationError = false"
          />
          <!--
            GH #215: masked, and never pre-filled in edit mode.

            The stored credential is no longer returned by getIntegration,
            so in edit mode this field opens blank and blank means "keep
            the current credential" (resolved server-side in
            edit_connection). The hint says so, because an empty password
            box on a Save form otherwise reads as "you must retype it".
          -->
          <v-text-field
            data-testid="supplier-integration-password"
            type="password"
            :rules="passwordRules"
            variant="filled"
            :label="secretLabel"
            :hint="passwordHint"
            :persistent-hint="editMode"
            v-model="password"
            @keyup="displayValidationError = false"
          />
          <v-text-field
            v-if="!isSns"
            data-testid="supplier-integration-account-number"
            :rules="[validateNotEmpty, validateCredentials]"
            variant="filled"
            label="Account Number"
            v-model="accountNumber"
            @keyup="displayValidationError = false"
          />
        </v-form>
      </v-card-text>
      <v-card-actions class="pa-3">
        <div class="d-flex align-center" v-if="loading">
          <img
            src="@/assets/block-loading.svg"
            style="width: 35px; opacity: 0.1"
            alt=""
            class="block-loading"
          />
          <v-spacer />
          <div class="md-body-no-spacing mr-2 ml-2">
            Verifying connection ...
          </div>
          <v-spacer />
          <img
            src="@/assets/block-loading.svg"
            style="width: 35px; rotate: 90deg; opacity: 0.1"
            alt=""
            class="block-loading"
          />
        </div>
        <div class="d-flex align-center" v-else>
          <v-spacer />
          <v-btn v-if="displayConnectionSuccess" variant="flat" @click="close"
            >Okay</v-btn
          >
          <v-btn
            v-else-if="editMode"
            variant="flat"
            @click="verifyAndEditExistingConnection"
            :disabled="!valid"
            >Save</v-btn
          ><v-btn
            v-else
            variant="flat"
            @click="verifyAndCreateNewConnection"
            :disabled="!valid"
            >Connect</v-btn
          >
        </div>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { describeError } from "@/utils/safeErrorLog";
import rules from "@/mixins/rules";
import { mapGetters } from "vuex";
import helpers from "@/mixins/helpers";

// vendors.supplier_key for S&S Activewear.
const SUPPLIER_KEY_SNS = "ss_activewear";

export default {
  components: {},
  props: {},
  data() {
    return {
      show: false,
      integrationID: null,
      // Which supplier the form is for -- set by open(). The credential
      // fields and copy differ per supplier.
      supplierName: null,
      supplierKey: null,
      // Why the last check failed: "unavailable" (supplier unreachable),
      // "rate_limited", "save_failed" (a 5xx -- the server could not store
      // it), or null (the supplier rejected the credential).
      failureReason: null,
      mobile: window.innerWidth < 960,
      valid: false,

      username: null,
      password: null,
      accountNumber: null,
      shippingLabelCompanyName: null,
      shippingOption: null,
      ccLastFourDigits: null,

      connectionVerified: false,
      displayValidationError: false,
      displayConnectionSuccess: false,
      loading: false,
      editMode: false,
    };
  },

  watch: {
    show: function(newVal, oldVal) {
      let fieldsEmpty = !this.accountNumber && !this.password && !this.username;
      if (newVal && (this.displayValidationError || fieldsEmpty)) {
        // The form mounts with the dialog content, which can be after this
        // watcher first fires; there is nothing to reset yet in that case.
        this.$refs.form?.reset();
      }
    },
  },

  computed: {
    ...mapGetters(["getDistributorId", "isSuperAdmin"]),

    isSns() {
      return this.supplierKey === SUPPLIER_KEY_SNS;
    },

    displayName() {
      return this.supplierName || "supplier";
    },

    // First connection vs a credential edit say different things: an edit
    // is verified with the supplier before it is saved, and the account was
    // already connected.
    successMessage() {
      const name = this.displayName;
      return this.editMode
        ? `Your new ${name} credentials were verified and saved. Account-based pricing still applies to ${name} products in your stores.`
        : `Your ${name} account is connected. Account-based pricing now applies to ${name} products in your stores.`;
    },

    secretLabel() {
      return this.isSns ? "API Key" : "Password";
    },

    validateCredentials() {
      if (!this.displayValidationError) return true;
      // "Incorrect" only when the supplier actually said no. An outage or our
      // own rate limit would otherwise send the user to re-type a good key.
      if (this.failureReason === "unavailable") {
        return `Couldn't reach ${this.displayName} right now. Try again in a few minutes.`;
      }
      if (this.failureReason === "rate_limited") {
        return "Too many attempts. Try again later.";
      }
      if (this.failureReason === "save_failed") {
        return "Couldn't save your credentials. Try again.";
      }
      return this.isSns
        ? "Account number or API key incorrect"
        : "Username or password incorrect";
    },

    /**
     * GH #215: the Password field is required when CONNECTING, optional
     * when EDITING.
     *
     * On edit the field opens blank (the stored credential is no longer
     * sent to the client) and a blank submission means "keep the current
     * credential". If `validateNotEmpty` stayed bound in edit mode the
     * form would never validate, Save would stay disabled, and a user
     * could not change their account number without also retyping a
     * credential they can no longer see.
     *
     * Creating has no current credential to keep, so blank stays invalid.
     */
    passwordRules() {
      if (this.editMode) {
        return [this.validateCredentials];
      }
      return [this.validateNotEmpty, this.validateCredentials];
    },

    passwordHint() {
      return this.editMode
        ? `Leave blank to keep the current ${this.isSns ? "API key" : "password"}`
        : undefined;
    },
  },

  mixins: [rules, helpers],
  created() {
    window.addEventListener("resize", () => {
      let width = window.innerWidth;
      if (width < 960) {
        this.mobile = true;
      } else {
        this.mobile = false;
      }
    });
  },

  unmounted() {
    window.removeEventListener("resize", () => {
      let width = window.innerWidth;
      if (width < 960) {
        this.mobile = true;
      } else {
        this.mobile = false;
      }
    });
  },
  async mounted() {},

  methods: {
    // Map a failed request to a reason. Only a supplier's "no" may read as
    // "incorrect"; a 5xx is OUR failure to save (#4102), not a bad credential.
    failureReasonForError(err) {
      const status = err?.response?.status;
      if (status === 429) return "rate_limited";
      if (status >= 500) return "save_failed";
      return null;
    },

    async verifyAndCreateNewConnection() {
      this.loading = true;
      let response;
      try {
        response = await this.$axios.post(
          "/supplier_integrations/verifyAndCreateNewConnection",
          this.credentialBody({
            integrationID: this.integrationID,
            password: this.password,
            accountNumber: this.accountNumber,
            distributorID: this.getDistributorId,
          })
        );

        this.failureReason = response.data.reason || null;
        this.displayValidationError = !response.data.verified;
        this.displayConnectionSuccess = response.data.verified;
      } catch (err) {
        // Not logged: the axios error carries the request body, i.e. the key.
        this.failureReason = this.failureReasonForError(err);
        this.displayValidationError = true;
      } finally {
        //console.log(response);
        this.loading = false;
      }
      await this.showRejection();
    },

    async verifyAndEditExistingConnection() {
      this.loading = true;
      let response;
      try {
        response = await this.$axios.post(
          "/supplier_integrations/verifyAndEditConnection",
          this.credentialBody({
            integrationID: this.integrationID,
            password: this.password,
            accountNumber: this.accountNumber,
            shippingLabelCompanyName: this.shippingLabelCompanyName,
            shippingOption: this.shippingOption,
            ccLastFourDigits: this.ccLastFourDigits,
            distributorID: this.getDistributorId,
          })
        );

        this.failureReason = response.data.reason || null;
        this.displayValidationError = !response.data.verified;
        this.displayConnectionSuccess = response.data.verified;
      } catch (err) {
        // Not logged: the axios error carries the request body, i.e. the key.
        this.failureReason = this.failureReasonForError(err);
        this.displayValidationError = true;
      } finally {
        //console.log(response);
        this.loading = false;
      }
      await this.showRejection();
    },

    // The "incorrect" message is a field rule reading displayValidationError,
    // and Vuetify does not re-run rules when that flag flips -- so without
    // this a rejected credential failed silently. Re-validate to show it.
    async showRejection() {
      if (!this.displayValidationError) return;
      await this.$nextTick();
      this.$refs.form?.validate();
    },

    // S&S has no username field; the server derives it from the account
    // number, so none is sent.
    credentialBody(body) {
      return this.isSns ? body : { ...body, username: this.username };
    },

    async open(id, editMode = false, supplier = {}) {
      this.displayConnectionSuccess = false;
      this.integrationID = id;
      this.supplierName = supplier.name || null;
      this.supplierKey = supplier.supplierKey || null;
      this.editMode = editMode;
      if (this.editMode) {
        await this.loadIntegrationInfo();
      }
      this.show = true;
    },

    async loadIntegrationInfo() {
      let result;
      try {
        result = await this.$axios.get(
          `/supplier_integrations/getIntegration/${this.integrationID}/${this.getDistributorId}`
        );
        this.username = result.data.username;
        // GH #215: the password is deliberately NOT pre-filled. The
        // response no longer carries the credential at all, and the edit
        // path treats a blank submission as "keep the current one".
        this.accountNumber = result.data.account_number;
      } catch (err) {
        console.log(describeError(err));
      } finally {
      }
    },

    close() {
      if (this.displayConnectionSuccess) {
        this.$emit("connection-verified");
      }

      this.integrationID = null;
      this.supplierName = null;
      this.supplierKey = null;
      this.failureReason = null;
      this.valid = false;

      this.username = null;
      this.password = null;
      this.accountNumber = null;
      this.shippingLabelCompanyName = null;
      this.shippingOption = null;
      this.ccLastFourDigits = null;

      this.connectionVerified = false;
      this.displayValidationError = false;
      this.loading = false;
      this.editMode = false;
      this.show = false;
    },
  },
};
</script>

<style scoped></style>
